<template>
    <div class="table-responsive">
        <table class="table table-bordered table-sm table-hover">
            <thead class="thead-orange">
                <tr v-if="searchable">
                    <th :colspan="fields.length">
                        <b-form-input
                            placeholder="Buscar"
                            size="sm"
                            v-model="search"
                        ></b-form-input>
                    </th>
                </tr>
                <tr>
                    <th
                        v-for="field in tableFields"
                        :class="{ 'table-header-sortable': !!field.sortable }"
                        @click.prevent="headerClicked(field)"
                        :key="field.key"
                    >
                        {{ field.label }}
                        <span v-if="!!field.sortable">
                            <i
                                class="fa fa-sort-asc"
                                v-if="
                                    sortDir(field.key) === 'asc' &&
                                        currentSortKey === field.key
                                "
                            ></i>
                            <i
                                class="fa fa-sort-desc"
                                v-if="
                                    sortDir(field.key) === 'desc' &&
                                        currentSortKey === field.key
                                "
                            ></i>
                        </span>
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="!tableData.length">
                    <td :colspan="fields.length" class="text-center">
                        No hay datos
                    </td>
                </tr>
                <template v-for="(item, i) in tableData">
                    <tr
                        v-if="!!tableData.length"
                        :class="getClasses(item.classes || null, i)"
                        :key="`row_${i}`"
                    >
                        <slot name="row" :item="item" :index="i">
                            <td
                                v-for="(field, j) in fields"
                                :data-row="i"
                                class="actions"
                                :key="`col_${j}`"
                            >
                                <app-nobr v-if="field.key === 'actions'">
                                    <slot
                                        :name="field.key"
                                        :item="item"
                                        :col="j"
                                        :row="i"
                                        :toggleDetails="
                                            $event => toggleDetails($event, i)
                                        "
                                        :row-details="item.rowDetails"
                                        :details-showing="item.detailsShowing"
                                    >
                                        {{ fieldValue(item, field.key) }}
                                    </slot>
                                </app-nobr>
                                <slot
                                    v-else
                                    :name="field.key"
                                    :item="item"
                                    :col="j"
                                    :row="i"
                                    :toggleDetails="
                                        $event => toggleDetails($event, i)
                                    "
                                    :row-details="item.rowDetails"
                                    :details-showing="item.detailsShowing"
                                >
                                    {{ fieldValue(item, field.key) }}
                                </slot>
                            </td>
                        </slot>
                    </tr>
                    <tr v-if="!!item.detailsShowing" :key="`detail_${i}`">
                        <td :colspan="fields.length">
                            <slot
                                name="row-details"
                                :item="item"
                                :toggleDetails="
                                    $event => toggleDetails($event, i)
                                "
                            ></slot>
                        </td>
                    </tr>
                </template>
            </tbody>
            <tfoot v-if="!!tableData.length && lastPage > 1">
                <td :colspan="fields.length">
                    <app-paginator
                        :current-page="currentPage"
                        :last-page="lastPage"
                        :from="from"
                        :to="to"
                        :total="total"
                        :per-page="perPage"
                        :on-change-page="onChangePage"
                    />
                </td>
            </tfoot>
        </table>
    </div>
</template>
<script>
/**
 * Eventos
 * @sort = Ordena por alguna columna cuando se da click en el encabezado. Envia ctx = {sortBy, sortDir}
 *
 */
import Paginator from '../Paginator/Paginator.vue'
export default {
    name: 'data-table',
    components: {
        appPaginator: Paginator,
    },
    props: {
        /**
         * Fields son los campos que va a tener como encabezado la tabla
         * key: el campo que se va a presentar en los registros de la tabla
         * orderBy: campo por el que se va a ordenar cuando le den click al encabezado
         * si el campo orderBy no se espeficica se usa el campo "key" para ordenar
         *
         */

        fields: {
            type: Array,
            default: () => [],
        },
        items: {
            type: Array,
            default: () => [],
        },
        currentPage: {
            type: Number,
            default: 1,
        },
        lastPage: {
            type: Number,
            default: 1,
        },
        from: {
            type: Number,
            default: 1,
        },
        to: {
            type: Number,
            default: 1,
        },
        total: {
            type: Number,
            default: 1,
        },
        perPage: {
            type: [String, Number],
            default: '10',
        },
        onChangePage: {
            type: Function,
            default: null,
        },
        searchable: {
            type: Boolean,
            default: () => false,
        },
        striped: {
            type: Boolean,
            default: () => false,
        },
    },
    data() {
        return {
            sortBy: null,
            sortDesc: false,
            directions: [],
            currentSortKey: '',
            search: '',
            rows: [],
        }
    },
    watch: {
        items(val) {
            this.processItemsData(val)
        },
    },
    computed: {
        tableFields() {
            return this.fields.map(item => {
                const keySortable = !['id', 'actions'].includes(item.key)
                const propSortable = item.hasOwnProperty('sortable')
                    ? item.sortable
                    : true
                const sortable = keySortable && propSortable
                return { ...item, sortable }
            })
        },
        tableData() {
            return !!this.search
                ? this.rows.filter(
                      item =>
                          item.fullText.search(this.search.toLowerCase()) !== -1
                  )
                : this.rows
        },
        hasFilters() {
            return this.fields.some(item => !!item.filter)
        },
    },
    methods: {
        headerClicked(field) {
            if (!field.sortable) return false

            const index = this.directions.findIndex(
                item => item.field.key === field.key
            )
            const dir =
                -1 !== index && this.directions[index].dir === 'asc'
                    ? 'desc'
                    : 'asc'

            ;-1 === index && this.directions.push({ field, dir })
            ;-1 !== index && this.directions.splice(index, 1, { field, dir })

            const sortBy = field.orderBy || field.key

            this.currentSortKey = field.key

            this.$emit('sort', { sortBy, sortDir: dir })
        },
        sortDir(key) {
            const index = this.directions.findIndex(
                item => item.field.key === key
            )
            const dir = -1 !== index ? this.directions[index].dir : ''
            return dir
        },
        fieldValue(item, key) {
            return _.get(item, key, '')
        },
        getClasses(classes = '', index) {
            const oddClass = index % 2 === 0 && this.striped ? 'odd' : ''

            if (!classes) return oddClass

            if (Array.isArray(classes)) {
                const newClasses = [...classes, oddClass]

                return newClasses.join(' ')
            } else if (typeof classes === 'string') {
                return [classes, oddClass].join(' ')
            } else if (
                typeof classes === 'object' &&
                classes.constructor === Object
            ) {
                const keyClasess = Object.keys(classes).filter(
                    key => !!classes[key]
                )
                keyClasess.push(oddClass)
                return keyClasess.join(' ')
            }

            return ''
        },
        toggleDetails(e, index) {
            const item = { ...this.rows[index] }
            item.detailsShowing = !item.detailsShowing
            this.rows.splice(index, 1, item)
        },
        processItemsData(val) {
            const keys = this.fields.map(item => item.key)

            this.rows = val.map((item, index) => {
                const fullText = keys
                    .map(key => String(item[key]).toLowerCase())
                    .join(' ')

                const row = !!item._rowDetails
                    ? { ...item, fullText, detailsShowing: false }
                    : { ...item, fullText }

                return row
            })
        },
    },
    mounted() {
        this.processItemsData(this.items)
    },
}
</script>
<style scoped>
.table-header-sortable {
    cursor: pointer;
}
.odd {
    background-color: rgba(255, 118, 28, 0.1);
}
</style>
